import { FILE_SKELETON_ITEMS_COUNT } from "@/config/constants";

/**
 * Loading component for file attachments
 * Displays file skeleton items when files are being loaded
 *
 * How it works:
 * 1. Renders multiple skeleton items that mimic the appearance of file attachments
 * 2. Uses animation to indicate loading state
 * 3. Provides visual feedback that content is loading
 *
 * Key features:
 * - Animated skeleton UI for better user experience
 * - Multiple skeleton items to represent a list of file attachments
 * - Consistent styling with actual file attachment items
 * - Clear visual indication of loading state
 * - Uses configurable constant for skeleton items count
 *
 * Context:
 * This component is displayed by FileAttachmentList when file attachments are being loaded.
 * It follows the same skeleton pattern as MediaAttachmentLoading and
 * LinkLoading for consistency across the application.
 */
export default function FileAttachmentLoading() {
  // Create an array of 5 skeleton items
  const skeletonItems = Array.from(
    { length: FILE_SKELETON_ITEMS_COUNT },
    (_, index) => (
      <div key={index} className="flex w-full animate-pulse items-center gap-2">
        {/* File icon skeleton - matches the document icon */}
        <div className="relative size-10 shrink-0 rounded-sm bg-muted-foreground/5">
          <div className="absolute inset-0 size-full rounded-sm"></div>
        </div>

        {/* File details skeleton */}
        <div className="flex w-full min-w-0 flex-1 flex-col gap-1 overflow-hidden">
          {/* Filename skeleton */}
          <div className="h-5 w-3/4 rounded-md bg-muted"></div>
          {/* File size skeleton */}
          <div className="h-4 w-16 rounded-md bg-muted"></div>
        </div>

        {/* Download button skeleton */}
        <div className="mx-[6px] size-6 shrink-0 rounded-full bg-muted-foreground/5"></div>
      </div>
    ),
  );

  return <div className="flex w-full flex-col gap-2">{skeletonItems}</div>;
}
